import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import heroAsset from "@/assets/blog/pcos-explained-symptoms-diagnosis-india.webp";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";

const URL = "https://truwe.in/blog/pcos-explained-symptoms-diagnosis-india";
const TITLE = "PCOS Explained: Symptoms, Diagnosis and What to Do Next | TruWe";
const PUBLISHED = "2026-08-13";

export const Route = createFileRoute("/blog/pcos-explained-symptoms-diagnosis-india")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: "pcos-explained-symptoms-diagnosis-india",
      title: TITLE,
      description:
        "A calm India-focused guide to PCOS symptoms, diagnosis, PCOD vs PCOS, fertility, diet, and when to speak with a doctor.",
      ogTitle: "PCOS Explained: Symptoms, Diagnosis & Next Steps",
      ogDescription:
        "Irregular periods, acne, facial hair or confusing scan results? Here is what PCOS means and what to do next.",
      headline: "PCOS Explained: Symptoms, Diagnosis and What to Do Next",
      breadcrumbName: "PCOS Explained",
      datePublished: PUBLISHED,
      dateModified: PUBLISHED,
      ogImage: `https://truwe.in${heroAsset}`,
      faqs: [
        {
          q: "What is the main cause of PCOS?",
          a: "There is no single known cause. Genetics, androgen regulation, ovulation and metabolism can all contribute, and the mix differs between individuals.",
        },
        {
          q: "Why am I not getting periods regularly?",
          a: "PCOS is one possible cause, but pregnancy, thyroid disorders, high prolactin, major changes in energy intake or exercise, stress and other conditions can also disrupt cycles. A clinician can help identify the reason.",
        },
        {
          q: "Can I have PCOS if my ultrasound is normal?",
          a: "Yes. In adults, PCOS can be diagnosed without polycystic ovarian morphology when irregular ovulation and evidence of higher androgen activity are present and other causes have been excluded.",
        },
        {
          q: "Does having polycystic ovaries on ultrasound mean I have PCOS?",
          a: "Not by itself. The scan finding must be interpreted alongside cycle patterns, androgen signs or blood tests and other possible causes.",
        },
        {
          q: "Is PCOS the same as PCOD?",
          a: "PCOD is commonly used in India, but current international clinical guidelines use PCOS and do not define PCOD as a separate diagnosis.",
        },
        {
          q: "Can PCOS go away after weight loss?",
          a: "Symptoms and metabolic health may improve with sustainable lifestyle changes, but PCOS is a long-term condition and is not diagnosed or cured by body weight alone.",
        },
        {
          q: "Which supplement is best for PCOS in India?",
          a: "There is no universally best supplement. The choice depends on your symptoms, diet, medicines, test results and pregnancy plans. Current guidance says evidence for inositol is limited and does not support one specific type or dose for everyone.",
        },
      ],
    }),
});

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Hormonal Health
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            PCOS Explained: Symptoms, Diagnosis and What to Do Next
          </h1>
          <BlogByline date={PUBLISHED} readingTime="12 min read" />
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Irregular periods, acne, facial hair or a confusing ultrasound report? Here is
            what PCOS means, how doctors diagnose it and the next steps that genuinely help.
          </p>
          <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={heroAsset}
              alt="A woman reading a clear guide about PCOS symptoms and diagnosis"
              width={1200}
              height={630}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-8 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            Your period is late again. The acne you thought you had left behind is back. A few
            dark hairs have appeared on your chin, your hairbrush looks fuller than usual—and
            ten open tabs are telling you ten different things.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            Take a breath. PCOS can feel confusing, but understanding it does not have to be.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            This guide is for the woman who has just searched “what is PCOS?” and for the
            woman who already has a diagnosis but still wants someone to explain the whole
            picture in plain English. No panic. No blame. No promise of a miracle cure.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <aside className="mt-8 rounded-2xl border border-brand/20 bg-brand/[0.04] p-5 md:p-6">
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
              <Sparkles className="h-3.5 w-3.5" />
              Key takeaway
            </p>
            <p className="mt-2 text-base leading-relaxed text-brand-deep">
              PCOS is a common hormonal and metabolic condition that can affect periods,
              ovulation, skin, hair and long-term health. It looks different from one person
              to another, and a proper diagnosis requires more than a Google search or a
              single scan.
            </p>
          </aside>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
            What is PCOS, exactly?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            PCOS stands for polycystic ovary syndrome. The name is slightly misleading: you do
            not need to have ovarian cysts to have PCOS, and the “cysts” seen on scans are
            usually small, immature follicles rather than dangerous cysts.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            At the centre of PCOS are two systems that often overlap: reproductive hormones and
            metabolism. Some people have signs of higher androgen activity, such as facial hair
            or acne. Some ovulate irregularly and have long or missed cycles. Some also have
            insulin resistance or other metabolic risk factors. Not everyone has every feature.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            The World Health Organization estimates that PCOS affects about 10–13% of women of
            reproductive age worldwide, and many remain undiagnosed.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
            PCOD vs PCOS: is there really a difference?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            In India, “PCOD” and “PCOS” are often used as if they describe two different
            conditions. Current international clinical guidelines use the term PCOS; they do
            not define PCOD as a separate, milder diagnosis.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            In 2026, international consensus introduced PMOS as the new name for PCOS; the
            transition is underway. Read what changed in{" "}
            <Link
              to="/blog/pcod-vs-pcos-vs-pmos"
              className="font-semibold text-brand underline underline-offset-2"
            >
              PCOD vs PCOS vs PMOS
            </Link>
            .
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            Someone may have polycystic-looking ovaries on an ultrasound without having the
            syndrome. Equally, someone can have PCOS without polycystic ovaries on a scan. That
            is why the whole pattern matters more than the label used in casual conversation.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
            Common PCOS symptoms in women
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            PCOS does not arrive with one universal checklist. The symptoms that usually bring
            someone to a gynaecologist or endocrinologist include:
          </p>
          <ul className="mt-4 space-y-2">
            {[
              "Periods that are infrequent, unpredictable or absent",
              "Difficulty predicting ovulation or becoming pregnant",
              "Excess facial or body hair",
              "Persistent acne or oily skin",
              "Hair thinning on the scalp",
              "Weight gain or difficulty managing weight for some people",
              "Dark, velvety patches of skin, especially around the neck or underarms, which can be associated with insulin resistance",
            ].map((item, i) => (
              <li
                key={i}
                className="flex gap-2.5 text-base leading-relaxed text-muted-foreground"
              >
                <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                <span className="min-w-0 break-words">{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            Fatigue, anxiety and low mood can sit alongside PCOS, but they are not enough to
            diagnose it. And symptoms do not have to begin at one particular age. There is no
            special “PCOS at 22”, “PCOS at 25” or “PCOS at 28”—the same condition can become
            noticeable at different life stages.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
            How do I know if I have PCOS?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            You cannot confirm PCOS from symptoms alone. In adults, clinicians generally look
            for at least two of three features after ruling out other possible causes:
          </p>
          <ol className="mt-4 space-y-2">
            {[
              "Irregular or absent ovulation, often seen as irregular or missed periods",
              "Clinical or blood-test evidence of higher androgen activity",
              "Polycystic ovarian morphology on ultrasound; in some adult diagnostic pathways, an AMH blood test may be used instead of ultrasound",
            ].map((item, i) => (
              <li
                key={i}
                className="flex gap-3 text-base leading-relaxed text-muted-foreground"
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/10 text-[11px] font-bold text-brand">
                  {i + 1}
                </span>
                <span className="min-w-0 break-words">{item}</span>
              </li>
            ))}
          </ol>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            If irregular cycles and clear hyperandrogenism are already present, an ultrasound
            may not be needed. Diagnosis is different in adolescents, where normal puberty can
            resemble PCOS and ultrasound is not recommended as an early shortcut.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            Your clinician may consider pregnancy, thyroid conditions, high prolactin,
            non-classic congenital adrenal hyperplasia and other causes before confirming
            PCOS. Blood pressure, glucose status, cholesterol, sleep and emotional wellbeing may
            also be reviewed because PCOS affects more than periods.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
            “My ultrasound shows polycystic ovaries.” What does that mean?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            It means the scan has found a particular ovarian pattern. It does not
            automatically mean you have PCOS.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            This is one of the most useful distinctions to remember: an ultrasound finding is
            one piece of evidence, not the entire diagnosis. Ask the clinician to interpret it
            alongside your cycle history, symptoms, examination and blood tests.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
            Is PCOS permanent or curable?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            PCOS is considered a long-term condition, and there is currently no single cure.
            That sentence can sound frightening, but “long-term” does not mean “nothing will
            improve.”
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            Symptoms and health risks can often be managed with a plan tailored to your needs.
            Cycles may become more predictable. Acne or unwanted hair may improve. Metabolic
            markers can change. Fertility support is available. The right goal is not to chase
            a permanent “reversal” claim; it is to build care that works for your body and your
            current life stage.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
            Will I be able to get pregnant with PCOS?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            Many people with PCOS become pregnant, naturally or with support. Irregular
            ovulation can make timing harder, and PCOS is linked with a higher risk of some
            pregnancy complications, so personalised preconception care matters.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            If pregnancy is a goal, say so early in the conversation with your clinician. A
            treatment that makes sense for acne or cycle control may be different from a plan
            designed to support ovulation and conception.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
            Can stress cause PCOS?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            Stress alone is not considered the cause of PCOS. But chronic stress can disturb
            sleep, appetite, energy and menstrual patterns, which may make symptoms feel
            louder. Stress management is useful support—not an explanation that puts the blame
            on you.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
            A beginner-friendly PCOS food approach
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            There is no universally correct “PCOS diet chart”, and you do not need to fear
            every bowl of rice or every roti. A practical Indian plate can be much simpler:
          </p>
          <ul className="mt-4 space-y-2">
            {[
              "Add a protein source: dal, chana, rajma, paneer, curd, eggs, fish, chicken or tofu",
              "Add fibre and colour: sabzi, salad, fruit, whole grains and legumes",
              "Pair carbohydrates with protein, fibre or healthy fat instead of eating them alone",
              "Keep sugary drinks and highly processed snack foods occasional rather than automatic",
              "Choose a pattern you can repeat; consistency is more useful than a seven-day “detox”",
            ].map((item, i) => (
              <li
                key={i}
                className="flex gap-2.5 text-base leading-relaxed text-muted-foreground"
              >
                <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                <span className="min-w-0 break-words">{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            Regular movement matters too. Walking, strength training, sport, yoga or other
            activity can all count. The best routine is one you can sustain without turning
            your body into a punishment project.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
            What about “natural PCOS treatments” and supplements?
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            This is where search results become especially noisy. Phrases such as “PCOS
            supplements that actually work”, “best medicine for PCOS without side effects” and
            “PCOS weight-loss supplements” sound reassuring—but no product works for everyone, and
            “natural” does not mean risk-free.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            The 2023 international guideline says inositol may be considered based on
            individual preferences, with limited harm and possible metabolic benefits, but
            clinical benefits are limited and no specific type, dose or combination can
            currently be recommended because the evidence quality is not strong enough.
            Berberine can affect blood glucose and interact with medicines. Magnesium is most
            relevant when intake is low or a deficiency exists. NAC = standard. Supplement
            decisions should sit beside—not replace—appropriate medical care.
          </p>
          <aside className="mt-8 rounded-2xl border border-brand/20 bg-brand/[0.04] p-5 md:p-6">
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
              <Sparkles className="h-3.5 w-3.5" />
              Key takeaway
            </p>
            <p className="mt-2 text-base leading-relaxed text-brand-deep">
              Before starting any PCOS supplement, check four things: the exact ingredients
              and doses, whether the claim matches the evidence, possible medicine
              interactions, and whether pregnancy or trying to conceive changes the safety
              decision.
            </p>
          </aside>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
            Where Ova Balance fits
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            Ova Balance is TruWe’s once-daily sachet with a 40:1 combination of myo-inositol
            and D-chiro-inositol, plus berberine HCl, L-5-MTHF, magnesium, zinc and chromium. It
            is designed as nutritional support—not as a cure, a replacement for diagnosis or
            a substitute for clinician-led treatment.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            If you are considering it, read the full label and discuss it with your clinician if
            you take metformin, insulin or other glucose-lowering medicines; use hormonal
            medication; are pregnant or breastfeeding; or are trying to conceive.
          </p>
          <Link
            to="/products/$handle"
            params={{ handle: "ova-balance" }}
            className="group mt-6 flex flex-col gap-2 rounded-3xl border border-border bg-card p-6 transition hover:-translate-y-0.5 hover:border-brand/30 hover:shadow-luxury"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
              From the TruWe range
            </p>
            <p className="font-display text-xl font-bold text-brand-deep group-hover:text-brand">
              Ova Balance
            </p>
            <p className="text-sm text-muted-foreground">
              Explore Ova Balance ingredients and label
            </p>
            <span className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-brand">
              View product <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </Link>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
            What to do next if this sounds familiar
          </h2>
          <ol className="mt-4 space-y-2">
            {[
              "Track your cycle dates and the symptoms that concern you.",
              "Book a consultation with a gynaecologist, endocrinologist or qualified clinician.",
              "Bring previous blood tests, scan reports and a list of medicines or supplements.",
              "Ask what has been ruled out—not only what has been diagnosed.",
              "Agree on one or two realistic goals: cycle protection, acne, unwanted hair, metabolic health, fertility or emotional wellbeing.",
            ].map((item, i) => (
              <li
                key={i}
                className="flex gap-3 text-base leading-relaxed text-muted-foreground"
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/10 text-[11px] font-bold text-brand">
                  {i + 1}
                </span>
                <span className="min-w-0 break-words">{item}</span>
              </li>
            ))}
          </ol>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
            Your body is not broken, and PCOS is not a personal failure. You deserve a diagnosis
            that is careful, a plan that is realistic and explanations that make sense without
            requiring another ten open tabs.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <section className="mt-14">
            <h2 className="font-display text-2xl font-extrabold text-brand-deep md:text-3xl">
              Frequently asked questions
            </h2>
            <div className="mt-4 space-y-3">
              {[
                {
                  q: "What is the main cause of PCOS?",
                  a: "There is no single known cause. Genetics, androgen regulation, ovulation and metabolism can all contribute, and the mix differs between individuals.",
                },
                {
                  q: "Why am I not getting periods regularly?",
                  a: "PCOS is one possible cause, but pregnancy, thyroid disorders, high prolactin, major changes in energy intake or exercise, stress and other conditions can also disrupt cycles. A clinician can help identify the reason.",
                },
                {
                  q: "Can I have PCOS if my ultrasound is normal?",
                  a: "Yes. In adults, PCOS can be diagnosed without polycystic ovarian morphology when irregular ovulation and evidence of higher androgen activity are present and other causes have been excluded.",
                },
                {
                  q: "Does having polycystic ovaries on ultrasound mean I have PCOS?",
                  a: "Not by itself. The scan finding must be interpreted alongside cycle patterns, androgen signs or blood tests and other possible causes.",
                },
                {
                  q: "Is PCOS the same as PCOD?",
                  a: "PCOD is commonly used in India, but current international clinical guidelines use PCOS and do not define PCOD as a separate diagnosis.",
                },
                {
                  q: "Can PCOS go away after weight loss?",
                  a: "Symptoms and metabolic health may improve with sustainable lifestyle changes, but PCOS is a long-term condition and is not diagnosed or cured by body weight alone.",
                },
                {
                  q: "Which supplement is best for PCOS in India?",
                  a: "There is no universally best supplement. The choice depends on your symptoms, diet, medicines, test results and pregnancy plans. Current guidance says evidence for inositol is limited and does not support one specific type or dose for everyone.",
                },
              ].map((f, i) => (
                <div key={i} className="rounded-2xl border border-border bg-card p-5">
                  <h3 className="font-display text-base font-bold text-brand-deep md:text-lg">
                    {f.q}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        <Reveal delay={0.05}>
          <section className="mt-14">
            <h2 className="font-display text-2xl font-extrabold text-brand-deep md:text-3xl">
              Sources
            </h2>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
              <li>
                World Health Organization.{" "}
                <strong>Polycystic ovary syndrome.</strong> Updated 22 January 2026.{" "}
                <a
                  href="https://www.who.int/news-room/fact-sheets/detail/polycystic-ovary-syndrome"
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  className="underline"
                >
                  https://www.who.int/news-room/fact-sheets/detail/polycystic-ovary-syndrome
                </a>
              </li>
              <li>
                Teede HJ, Tay CT, Laven JJE, et al.{" "}
                <strong>
                  Recommendations from the 2023 International Evidence-based Guideline for the
                  Assessment and Management of Polycystic Ovary Syndrome.
                </strong>{" "}
                American Society for Reproductive Medicine.{" "}
                <a
                  href="https://www.asrm.org/practice-guidance/practice-committee-documents/recommendations-from-the-2023-international-evidence-based-guideline-for-the-assessment-and-management-of-polycystic-ovary-syndrome/"
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  className="underline"
                >
                  ASRM practice guidance
                </a>
              </li>
              <li>
                Monash Centre for Health Research and Implementation.{" "}
                <strong>
                  International Evidence-based Guideline for the Assessment and Management of
                  Polycystic Ovary Syndrome 2023.
                </strong>{" "}
                <a
                  href="https://www.monash.edu/__data/assets/pdf_file/0003/3379521/Evidence-Based-Guidelines-2023.pdf"
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  className="underline"
                >
                  PDF
                </a>
              </li>
              <li>
                American College of Obstetricians and Gynecologists.{" "}
                <strong>Polycystic Ovary Syndrome (PCOS).</strong>{" "}
                <a
                  href="https://www.acog.org/womens-health/faqs/polycystic-ovary-syndrome-pcos"
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  className="underline"
                >
                  https://www.acog.org/womens-health/faqs/polycystic-ovary-syndrome-pcos
                </a>
              </li>
            </ol>
          </section>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="hormonal" />
          <ClusterLinks cluster="hormonal" />
          <AuthorityFlow cluster="hormonal" />
          <RelatedReading articles={relatedArticles("pcos-explained-symptoms-diagnosis-india")} />
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            Educational only. This article does not diagnose or treat PCOS. Seek personalised
            advice from a qualified clinician, especially if periods stop for three months,
            bleeding is very heavy, symptoms change rapidly, pregnancy is possible, or you are
            trying to conceive.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
